import type React from "react";
import { Skeleton } from "@/components/ui/skeleton";

export interface LoadingSkeletonProps {
  /** Shape to mimic while data loads, so the layout doesn't jump once it
   * arrives. Defaults to "text" — a couple of lines, for anywhere a plain
   * "Loading…" string doesn't need to hold a specific shape. */
  variant?: "kpis" | "chart" | "table" | "text";
  /** table variant only — number of skeleton rows. */
  rows?: number;
}

function KpisSkeleton(): React.JSX.Element {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-[10px]">
      {Array.from({ length: 4 }, (_, i) => (
        <div key={i} className="card-duress p-4">
          <Skeleton className="h-3 w-16 mb-3" />
          <Skeleton className="h-7 w-14" />
        </div>
      ))}
    </div>
  );
}

function ChartSkeleton(): React.JSX.Element {
  return (
    <div className="card-duress p-5">
      <Skeleton className="h-4 w-40 mb-4" />
      <Skeleton className="h-[220px] w-full" />
    </div>
  );
}

function TableSkeleton({ rows = 6 }: { rows: number }): React.JSX.Element {
  return (
    <div className="card-duress p-5">
      <Skeleton className="h-8 w-64 mb-4" />
      <div className="space-y-2.5">
        {Array.from({ length: rows }, (_, i) => (
          <Skeleton key={i} className="h-8 w-full" />
        ))}
      </div>
    </div>
  );
}

function TextSkeleton(): React.JSX.Element {
  return (
    <div className="space-y-2 py-8">
      <Skeleton className="h-4 w-48" />
      <Skeleton className="h-4 w-32" />
    </div>
  );
}

export function LoadingSkeleton({
  variant = "text",
  rows = 6,
}: LoadingSkeletonProps): React.JSX.Element {
  switch (variant) {
    case "kpis":
      return <KpisSkeleton />;
    case "chart":
      return <ChartSkeleton />;
    case "table":
      return <TableSkeleton rows={rows} />;
    case "text":
      return <TextSkeleton />;
  }
}
